import React from "react";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { formatCLP } from "@/lib/format";
import { computeEconomics } from "@/pages/wizard/economics";

function age(b) { if (!b) return null; const d = new Date(b), t = new Date(); let a = t.getFullYear() - d.getFullYear(); if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a--; return isNaN(a) ? null : a; }

function Section({ title, onEdit, children, testId }) {
  return (
    <section className="bg-white rounded-2xl border border-border p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <Button variant="ghost" size="sm" onClick={onEdit} className="text-primary" data-testid={`edit-${testId}`}><Pencil className="w-4 h-4 mr-1" /> Editar</Button>
      </div>
      {children}
    </section>
  );
}

export default function StepReview({ app, goStep }) {
  const fam = app.family || {};
  const eco = computeEconomics(app);
  const income = app.income || { adults: [], others: [] };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1">Revisión</h1>
        <p className="text-muted-foreground">Revisa el resumen antes de enviar. Puedes editar cualquier sección.</p>
      </div>

      <Section title="Grupo familiar" onEdit={() => goStep(1)} testId="review-family">
        <ul className="text-sm space-y-1">
          <li><strong>{fam.titular?.nombre}</strong> · {fam.titular?.rut} {age(fam.titular?.birthdate) != null ? `· ${age(fam.titular?.birthdate)} años` : ""}</li>
          {fam.spouse?.nombre && <li>{fam.spouse.nombre} · {fam.spouse.rut} (cónyuge/pareja)</li>}
          {(fam.children || []).map((c, i) => <li key={i}>{c.nombre} · {c.rut} {age(c.birthdate) != null ? `· ${age(c.birthdate)} años` : ""} {c.colegio ? `· ${c.colegio}` : ""}</li>)}
        </ul>
      </Section>

      <Section title="Ingresos" onEdit={() => goStep(2)} testId="review-income">
        <ul className="text-sm space-y-1">
          {income.adults.map((a, i) => (
            <li key={i} className="flex justify-between">
              <span>{a.nombre}</span>
              <span>
                Declarado: {formatCLP(a.income_declared)}
                {a.income_detected != null && <span className={a.verification_status === "mismatch" ? "text-amber-700" : "text-emerald-700"}> · Verificado: {formatCLP(a.income_detected)}</span>}
              </span>
            </li>
          ))}
          {(income.others || []).map((o, i) => <li key={`o${i}`} className="flex justify-between"><span>{o.tipo} {o.descripcion}</span><span>{formatCLP(o.amount)}</span></li>)}
        </ul>
      </Section>

      <Section title="Gastos extraordinarios" onEdit={() => goStep(3)} testId="review-expenses">
        {(app.expenses || []).length === 0 ? <p className="text-sm text-muted-foreground">Sin gastos declarados.</p> : (
          <ul className="text-sm space-y-1">
            {app.expenses.map((e, i) => <li key={i} className="flex justify-between gap-3"><span>{e.category}{e.description ? ` — ${e.description}` : (e.related_person ? ` — ${e.related_person}` : "")}</span><span className="shrink-0">{formatCLP(e.amount)}</span></li>)}
            <li className="flex justify-between border-t border-border pt-2 font-semibold"><span>Total</span><span>{formatCLP(eco.expenses)}</span></li>
          </ul>
        )}
      </Section>

      <section className="bg-primary text-white rounded-2xl p-6">
        <h3 className="text-white text-base font-semibold mb-4">Resumen económico</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span>Ingreso familiar bruto</span><span className="font-semibold">{formatCLP(eco.gross)}</span></div>
          <div className="flex justify-between"><span>Gastos extraordinarios considerados</span><span className="font-semibold">− {formatCLP(eco.expenses)}</span></div>
          <div className="flex justify-between border-t border-white/20 pt-2 text-base"><span>Ingreso familiar ajustado</span><span className="font-bold text-accent">{formatCLP(eco.adjusted)}</span></div>
        </div>
      </section>
    </div>
  );
}
